import type {
  BookingProposalSummary,
  EventsBooking,
  EventsCatalogItem,
  EventsTimelineEntry,
  PricedLine,
  PricingResult,
  ProposalLineInput,
  ProposalVersion,
  ProposalVersionState,
} from './api/types';
import type { Tone } from './leads';
import { formatDuration } from './settings';

export function formatMoney(cents: number, currency = 'USD'): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
  }).format(cents / 100);
}

export function formatRate(rateBp: number): string {
  return `${(rateBp / 100).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  })}%`;
}

export const VERSION_STATE_LABEL: Record<ProposalVersionState, string> = {
  DRAFT: 'Draft',
  PUBLISHED: 'Sent',
  ACCEPTED: 'Accepted',
  SUPERSEDED: 'Replaced',
  DECLINED: 'Declined',
};

export function pricingOf(version: ProposalVersion): PricingResult | null {
  return 'grand_total_cents' in version.pricing_snapshot
    ? (version.pricing_snapshot as PricingResult)
    : null;
}

export function introOf(version: ProposalVersion): string {
  return version.blocks.find((block) => block.kind === 'INTRO')?.text ?? '';
}

export function isExpired(version: ProposalVersion, now: Date): boolean {
  return (
    version.state === 'PUBLISHED' &&
    version.expires_at !== null &&
    new Date(version.expires_at) <= now
  );
}

/** Why no proposal can be drafted, changed or sent for this booking. */
export function proposalLockReason(booking: EventsBooking): string | null {
  if (booking.sales_stage === 'LOST') {
    return 'This booking is marked as lost, so its proposal can’t change.';
  }
  if (booking.contract_state === 'SIGNED') {
    return 'The guest accepted the proposal, so it can’t change.';
  }
  if (booking.lifecycle_state !== 'PROSPECT') {
    return 'Only bookings that aren’t confirmed or cancelled can have their proposal changed.';
  }
  return null;
}

/**
 * Mirrors commercial/proposals/commands.py::_require_publish_inventory for
 * the part the booking row shows: an event that needs a space must have an
 * active, unexpired hold on each of its spaces. The backend also checks the
 * hold covers the interval and remains the authority.
 */
export function publishInventoryProblem(
  booking: EventsBooking,
  now: Date,
): string | null {
  const event = booking.event;
  if (!event || event.requires_space === false) return null;
  const spaceIds = (event.spaces ?? []).map((space) => space.id);
  if (spaceIds.length === 0) {
    return 'Choose a space for this event before sending the proposal.';
  }
  const held = new Set(
    (event.holds ?? [])
      .filter(
        (hold) =>
          hold.status === 'ACTIVE' &&
          (hold.kind === 'HARD' ||
            hold.expires_at === null ||
            new Date(hold.expires_at) > now),
      )
      .map((hold) => hold.space),
  );
  return spaceIds.every((id) => held.has(id))
    ? null
    : 'The space hold has expired or was released. Place a new hold before sending the proposal.';
}

export type ProposalStage =
  | { kind: 'none' }
  | { kind: 'draft'; draft: ProposalVersion; current: ProposalVersion | null }
  | {
      kind: 'awaiting-approval';
      draft: ProposalVersion;
      current: ProposalVersion | null;
    }
  | { kind: 'sent'; current: ProposalVersion }
  | { kind: 'expired'; current: ProposalVersion }
  | { kind: 'accepted'; current: ProposalVersion }
  | { kind: 'declined'; current: ProposalVersion };

export function proposalStage(
  summary: BookingProposalSummary,
  now: Date,
): ProposalStage {
  const proposal = summary.proposal;
  if (!proposal) return { kind: 'none' };
  const byId = (id: number | null) =>
    proposal.versions.find((version) => version.id === id) ?? null;
  const draft = byId(proposal.draft_version_id);
  const current = byId(proposal.current_version_id);
  if (current?.state === 'ACCEPTED') return { kind: 'accepted', current };
  if (current?.state === 'DECLINED' && !draft) {
    return { kind: 'declined', current };
  }
  if (draft) {
    return proposal.pending_publish_approval_id !== null
      ? { kind: 'awaiting-approval', draft, current }
      : { kind: 'draft', draft, current };
  }
  if (current) {
    return isExpired(current, now)
      ? { kind: 'expired', current }
      : { kind: 'sent', current };
  }
  return { kind: 'none' };
}

export type EditorLine = {
  /** Stable React key; not sent. */
  key: string;
  code: string;
  /** Guests, or 1 for a flat line. Unused for hourly lines. */
  quantity: string;
  /** Minutes booked. Used only for hourly lines. */
  durationMinutes: string;
  /** People. Used only for hourly staffing. */
  headcount: string;
};

let nextKey = 0;
export function newEditorLine(
  code = '',
  quantity = '',
  extras?: { durationMinutes?: string; headcount?: string },
): EditorLine {
  nextKey += 1;
  return {
    key: `line-${nextKey}`,
    code,
    quantity,
    durationMinutes: extras?.durationMinutes ?? '',
    headcount: extras?.headcount ?? '1',
  };
}

export function editorLinesFrom(version: ProposalVersion): EditorLine[] {
  return [...version.lines]
    .sort((a, b) => a.position - b.position)
    .map((line) =>
      line.pricing_mode === 'PER_HOUR'
        ? newEditorLine(line.code, '1', {
            durationMinutes: String(line.duration_minutes ?? ''),
            headcount: String(line.headcount ?? 1),
          })
        : newEditorLine(line.code, String(line.quantity)),
    );
}

/** Whole minutes between the event's start and end, when both are set. */
export function eventDurationMinutes(
  startsAt: string | null | undefined,
  endsAt: string | null | undefined,
): number | null {
  if (!startsAt || !endsAt) return null;
  const minutes = Math.round(
    (new Date(endsAt).getTime() - new Date(startsAt).getTime()) / 60_000,
  );
  return minutes > 0 ? minutes : null;
}

/** Flat items are priced once; the backend refuses any other quantity. */
export function defaultQuantity(
  item: EventsCatalogItem | undefined,
  guestCount: number | null | undefined,
): string {
  if (!item || item.pricing_mode === 'FLAT') return '1';
  return String(guestCount && guestCount > 0 ? guestCount : 1);
}

/** Hourly lines start from the event length when it is known, and one person. */
export function defaultHourly(eventMinutes: number | null | undefined): {
  durationMinutes: string;
  headcount: string;
} {
  return {
    durationMinutes: String(
      eventMinutes && eventMinutes > 0 ? eventMinutes : 60,
    ),
    headcount: '1',
  };
}

export type LinesValidation =
  | { ok: true; lines: ProposalLineInput[] }
  | { ok: false; message: string };

export function validateLines(
  rows: EditorLine[],
  catalog: EventsCatalogItem[],
): LinesValidation {
  if (rows.length === 0) {
    return { ok: false, message: 'Add at least one item.' };
  }
  const byCode = new Map(catalog.map((item) => [item.code, item]));
  const lines: ProposalLineInput[] = [];
  for (const row of rows) {
    if (!row.code)
      return { ok: false, message: 'Choose an item on every line.' };
    const item = byCode.get(row.code);
    if (!item || !item.active) {
      return {
        ok: false,
        message: `“${item?.name ?? row.code}” is no longer in the catalog. Remove it or choose another item.`,
      };
    }
    if (item.pricing_mode === 'PER_HOUR') {
      const duration = Number(row.durationMinutes);
      const headcount = Number(row.headcount);
      if (!Number.isInteger(duration) || duration < 1) {
        return {
          ok: false,
          message: `Enter the minutes for ${item.name}.`,
        };
      }
      if (!Number.isInteger(headcount) || headcount < 1) {
        return {
          ok: false,
          message: `Enter the headcount for ${item.name}.`,
        };
      }
      if (headcount !== 1 && item.kind !== 'STAFFING') {
        return {
          ok: false,
          message: `${item.name} is billed once per hour, not per person.`,
        };
      }
      lines.push({
        code: row.code,
        duration_minutes: duration,
        headcount,
      });
      continue;
    }
    const quantity = Number(row.quantity);
    if (!Number.isInteger(quantity) || quantity < 1) {
      return {
        ok: false,
        message: `Enter a whole-number quantity for ${item.name}.`,
      };
    }
    if (item.pricing_mode === 'FLAT' && quantity !== 1) {
      return {
        ok: false,
        message: `${item.name} is a flat price; its quantity is 1.`,
      };
    }
    lines.push({ code: row.code, quantity });
  }
  return { ok: true, lines };
}

function lineIdentity(line: ProposalLineInput): string {
  return 'duration_minutes' in line
    ? `${line.code}:h:${line.duration_minutes}:${line.headcount}`
    : `${line.code}:q:${line.quantity}`;
}

export function sameLines(
  a: ProposalLineInput[],
  b: ProposalLineInput[],
): boolean {
  return (
    a.length === b.length &&
    a.every((line, index) => lineIdentity(line) === lineIdentity(b[index]))
  );
}

export function linesOf(version: ProposalVersion): ProposalLineInput[] {
  return [...version.lines]
    .sort((a, b) => a.position - b.position)
    .map((line) =>
      line.pricing_mode === 'PER_HOUR'
        ? {
            code: line.code,
            duration_minutes: line.duration_minutes ?? 0,
            headcount: line.headcount ?? 1,
          }
        : { code: line.code, quantity: line.quantity },
    );
}

/** How a catalog price reads before a line is quoted. */
export function catalogRate(item: EventsCatalogItem, currency: string): string {
  const price = formatMoney(item.unit_price_cents, currency);
  if (item.pricing_mode === 'FLAT') {
    return item.covered_minutes
      ? `${price} flat · ${formatDuration(item.covered_minutes)}`
      : `${price} flat`;
  }
  if (item.pricing_mode === 'PER_HOUR') return `${price}/hr`;
  return `${price} / guest`;
}

/**
 * Guest, flat, and hourly lines share one measure. Hourly staff include the
 * headcount; a booking that rounds up names both the booked and billed time.
 */
export function lineMeasure(
  line: Pick<
    PricedLine,
    | 'pricing_mode'
    | 'quantity'
    | 'unit_price_cents'
    | 'headcount'
    | 'duration_minutes'
    | 'billing_increment_minutes'
    | 'covered_minutes'
  >,
  currency: string,
): string {
  const price = formatMoney(line.unit_price_cents, currency);
  if (line.pricing_mode === 'PER_PERSON') {
    return `${line.quantity} × ${price}`;
  }
  if (line.pricing_mode === 'FLAT') {
    return line.covered_minutes
      ? `Flat · ${formatDuration(line.covered_minutes)}`
      : 'Flat';
  }
  const increment = line.billing_increment_minutes ?? 60;
  const billed = line.quantity * increment;
  const headcount = line.headcount ?? 1;
  const measure =
    headcount === 1
      ? `${formatDuration(billed)} × ${price}/hr`
      : `${headcount} × ${formatDuration(billed)} × ${price}/hr`;
  const booked = line.duration_minutes;
  if (booked != null && booked !== billed) {
    return `${measure} (booked ${formatDuration(booked)})`;
  }
  return measure;
}

const TIMELINE_LABEL: Record<string, string> = {
  'lead.create': 'Inquiry received',
  'lead.write': 'Inquiry updated',
  'lead.qualify': 'Inquiry qualified',
  'lead.lose': 'Inquiry marked lost',
  'lead.convert': 'Booking created',
  'lead.extraction_update': 'Details filled in from the call',
  'hold.place': 'Space held',
  'hold.release': 'Hold released',
  'hold.extend': 'Hold extended',
  'hold.extend.payment': 'Hold extended for payment',
  'hold.expiring': 'Hold expiring soon',
  'hold.expire': 'Hold expired',
  'event.update': 'Event details changed',
  'proposal.publish.requested': 'Proposal sent for approval',
  'proposal.publish': 'Proposal sent to the guest',
  'proposal.publish.blocked': 'Approved proposal couldn’t be sent',
  'proposal.view': 'Guest opened the proposal',
  'proposal.change_requested': 'Guest asked for changes',
  'proposal.accept': 'Guest accepted the proposal',
  'proposal.accept.rejected': 'Guest acceptance refused',
  'approval.resolve': 'Approval decided',
  'approval.cancel': 'Approval cancelled',
  'payment.deposit.create': 'Deposit requested',
  'payment.checkout.create': 'Guest started checkout',
  'payment.captured': 'Payment received',
  'payment.request.past_due': 'Deposit past due',
  'event.confirm': 'Event confirmed',
  'confirmation.blocked': 'Confirmation blocked',
  'booking.lose': 'Booking marked lost',
  'booking.cancel': 'Cancellation started',
  'booking.cancel.finalize': 'Booking cancelled',
};

const PROPOSAL_EDIT_LABEL: Record<string, string> = {
  create: 'Proposal drafted',
  update: 'Proposal edited',
  revise: 'Proposal revision started',
};

export function timelineLabel(entry: EventsTimelineEntry): string {
  if (entry.type === 'proposal.edit') {
    const operation = String(entry.payload?.operation ?? '');
    return PROPOSAL_EDIT_LABEL[operation] ?? 'Proposal edited';
  }
  const known = TIMELINE_LABEL[entry.type];
  if (known) return known;
  const words = entry.type.replace(/[._]/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function timelineTone(entry: EventsTimelineEntry): Tone {
  if (/rejected|blocked|mismatch|failed|expire$|lose/.test(entry.type)) {
    return 'warning';
  }
  if (/accept$|captured|confirm$|publish$/.test(entry.type)) return 'success';
  return 'neutral';
}
